import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import moment from 'moment';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import deburr from 'lodash.deburr';
import { Table as AntdTable, Tooltip, notification } from 'antd';
import { SolutionOutlined, FileExcelOutlined, TeamOutlined, FileTextOutlined, LoadingOutlined } from '@ant-design/icons';
import { Icon, ColumnsFilterModal, Loader, TableHeader, Button, BurgerBtn, Table as InvTable } from 'react-inv-forms';

import { RootState } from '../../../redux/reducers';
import {
  getDataCsv,
  getDataStudentListWithMarks,
  getDataStudentListWithMarksCancel,
} from '../../../redux/dynamicTabs/stronaPrzedmiotu/actions';
import { setDataAfterSort, changeColumnsSorting, setDataAttendanceListAfterSort } from '../../../redux/dynamicTabs/actions';
import { saveColumnsUserSetup as studentSaveColumnsUserSetup } from '../../../redux/staticViews/students/actions';
import marksActions from '../../../redux/marks/actions';
import { StudentData } from '../../../redux/marks/types';
import { MarksData } from '../../../redux/marks/types';
import { isTabWithForm } from '../../../redux/dynamicTabs/helpers';
import { refreshSelectedRecords as refreshSelectedRecordsSaga } from '../../../redux/dynamicTabs/stronaPrzedmiotu/saga';
import { refreshSelectedData as refreshSelectedDataAction } from '../../../redux/dynamicTabs/stronaPrzedmiotu/actions';
import {
  openProtocolModal,
  generateSingleProtocol,
  openChangeLogModal,
  getStudentsToCopyFinalMarkModal,
} from '../../../redux/appConfig/actions';

import withNoAccessToResource from '../../../hoc/withNoAccessToResource';
import withCommonTableLogic, { WithCommonTableLogicProps } from '../../../hoc/withCommonTableLogic';
import withTableClickableModalsHandler, {
  WithTableClickableModalsHandlerProps,
} from '../../../hoc/withTableClickableModalsHandler';
import withContextSearch, { WithContextSearchProps } from '../../../hoc/withContextSearch';
import withMobileAsideHandler, { WithMobileAsideHandlerProps } from '../../../hoc/withMobileAsideHandler';
import withAntdConfigProvider from '../../../hoc/withAntdConfigProvider';
import withDynamicTabLauncher, { WithDynamicTabLauncherProps } from '../../../hoc/withDynamicTabLauncher';
import withDynamicTabStateProvider, { WithDynamicTabStateProviderProps } from '../../../hoc/withDynamicTabStateProvider';
import withDynamicTabTableActionsProvider, {
  WithDynamicTabTableActionsProviderProps,
} from '../../../hoc/withDynamicTabTableActionsProvider';
import withTableDataHandler from '../../../hooks/withTableDataHandler';

import StudentDetailsView from '../../features/StudentDetailsView';
import Filter from '../../features/Filter';
import { MySubjectsDetailsList } from '../../features/MySubjectsDetailsList';
import DataDestructuring from '../../../helpers/DataDestructuring';
import ColumnsAdapter from '../../../helpers/columnsAdapter';
import StringBuilder from '../../../helpers/StringBuilder';
import TableCellTypes from '../../../helpers/tableCellTypes';
import { translationFromFile } from '../../../helpers/translationFromFile';
import translationNewLineSeparator from '../../../helpers/translationNewLineSeparator';
import { cacheSyncRefreshTime } from '../../../settings';
import { studentListWithMarksColumns } from '../../../settings/tableColumns';

import {
  studentDetailsViewTranslations,
  columnsFilterModalTranslations,
  filterTranslations,
  tableDetailsViewTranslations,
} from '../../../settings/i18n/translations/packets';

import DynamicButtonContainer from '../../UI/DynamicButtonContainer';
import CacheInfo from '../../UI/CacheInfo';
import MobileAside from '../../UI/MobileAside';
import { variables } from '../../../styles/variables';
import { ContextSearch } from '../../../styles/sharedUIElemStyles';
import {
  DynamicTabContainer,
  PrintableBodyWrapper,
  AboveTableContainer,
  FlexBox,
  TableContainer,
  FilterContainer,
  TabRightPanel,
  HeaderWrapper,
  CustomScrollbarContainer,
} from '../../../styles/tabContainers';

import filterColumnsModal from '../../../images/filterColumnsModal.svg';

const { removeUnwantedColumns } = ColumnsAdapter;
const { openSignModal } = marksActions;

type Props = MapStateToProps &
  DynamicTabComponent &
  MapDispatchToProps &
  WithCommonTableLogicProps &
  WithTableClickableModalsHandlerProps &
  WithContextSearchProps &
  RouteComponentProps &
  WithMobileAsideHandlerProps &
  WithDynamicTabStateProviderProps<MySubjectsData> &
  WithDynamicTabTableActionsProviderProps &
  WithDynamicTabLauncherProps &
  WithTranslation;

const Table = withTableDataHandler(InvTable);

const mandatoryMarksColumns = ['ocenaWyliczona', 'ocenaKoncowa', 'formyZajec'];
/**
 * Komponent zakładki pojedynczego przedmiotu (strona-przedmiotu), na którą można wejść z poziomu zakładek Moje Przedmioty oraz Studenci.
 * Zawiera m.in. szczegółowe dane o przedmiocie oraz tabelę ze studentami.
 * Wszystkie właściwości komponentu pochodzą ze stanu aplikacji i komponentów wyższego rzędu.
 *
 */
export class SubjectPage extends React.PureComponent<Props> {
  private printableRef = React.createRef<HTMLDivElement>();
  private attendanceListRef = React.createRef<HTMLDivElement>();
  private studentsListWithMarksRef = React.createRef<HTMLDivElement>();
  private buttonStudentsListWithMarksRef = React.createRef<HTMLButtonElement>();
  private tableId = 'subject_page_list_to_display';

  componentDidUpdate(prevProps: Props): void {
    if (!isTabWithForm(this.props.state)) {
      const { duration, syncInProgress, error } = this.props.state.table.cache;
      const { przedmiotKod } = this.props.state.leftPanel.data;
      if (przedmiotKod && moment.now() - duration > cacheSyncRefreshTime && !syncInProgress && !error) {
        this.props.syncDataWithCache();
      }

      const prevPropsState = prevProps.state as DynamicTabWithTable<any>;
      if (
        this.props.state.studentWithMarksData?.data !== prevPropsState.studentWithMarksData?.data &&
        !isEmpty(this.props.state.studentWithMarksData?.data) &&
        !this.props.state.studentWithMarksData?.loading
      ) {
        this.buttonStudentsListWithMarksRef.current?.click();
      }
    }
  }

  componentWillUnmount(): void {
    this.props.getDataStudentListWithMarksCancel(this.props.id);
  }

  generateContent = (data: SingleObjectDataStructure, columns: SingleColumn[], studentData?: any): SingleObjectDataStructure => {
    const modifiedData = { ...data };
    if (studentData) {
      const tab = {
        ...this.props.state.leftPanel.data,
        tabId: this.props.id,
      };
      Object.entries(modifiedData).forEach(([key, value]) => {
        columns.forEach(col => {
          if (col.dataIndex === key) {
            // jako argument callback przekazywany jest generator, ponieważ w Saga ocen wykonywany jest na tym callbacku efekt "call", który, aby działał poprawnie - wymaga dostarczenia generatora.
            modifiedData[key] = TableCellTypes.generate(
              { type: col.type, data: value, row: studentData, tab },
              refreshSelectedRecordsSaga.bind(null, refreshSelectedDataAction(this.props.id, [studentData.studentId])),
            );
          }
        });
      });
    }
    return modifiedData;
  };

  onDataFilter = (value: string, filterData: Array<string>): void => {
    this.props.selectFilterValues({
      filterGroupName: value,
      filterValues: filterData,
    });
  };

  openModal = (list: StudentData[]): void => {
    const { semestrAkademicki, rokAkademicki, przedmiotKod, wydzialZlecajacyKod, wydzialZlecajacyNazwa, przedmiotNazwa } =
      this.props.state.leftPanel.data;
    this.props.openSignModal({
      studentList: list,
      semestrAkademicki,
      rokAkademicki,
      przedmiotKod,
      wydzialZlecajacyKod,
      wydzialZlecajacyNazwa,
      przedmiotNazwa,
      tabId: this.props.id,
    });
  };

  moveToOtherStudent = (targetStudentIndex: number): void => {
    if (!isTabWithForm(this.props.state)) {
      const { data } = this.props.state.table;
      if (targetStudentIndex < data.length || targetStudentIndex > -1) {
        const targetStudentData = data.find((student: any, idx: number) => idx === targetStudentIndex);
        if (targetStudentData) {
          const targetRow = document.querySelector(`#subject_page_list_to_display [data-row-key="${targetStudentData.key}"]`);
          if (targetRow) {
            targetRow.dispatchEvent(new Event('click', { bubbles: true }));
            this.props.allowScrollToTableRow();
          }
        }
      }
    }
  };

  openModalForGroupAction = (): void => {
    if (!isTabWithForm(this.props.state)) {
      const { selected, data } = this.props.state.table;
      const studentDataListTransformed: StudentData[] = data
        .filter(student => selected.includes(student.studentId))
        .filter(student => student.ocenaKoncowa)
        .filter(student => student.wpisujacyOceneKoncowaId === this.props.userId)
        .map(student => ({
          firstName: student.studentImie,
          lastName: student.studentNazwisko,
          albumId: student.studentAlbum,
          ocena: student.ocenaKoncowa,
          obligatoryjnaDataWystawienia: student.ocenaKoncowaDataTimestamp,
          studentWydzial: student.studentWydzial,
          studentId: student.studentId,
        }));
      if (!studentDataListTransformed.length) {
        notification.warn({
          message: this.props.t('SIGN_MARKS_MODAL_NO_STUDENTS_WITH_FINAL_MARK'),
          top: 100,
        });
      } else {
        this.openModal(studentDataListTransformed);
        if (selected.length !== studentDataListTransformed.length) {
          notification.warn({
            message: (
              <>
                {this.props.t('SIGN_MARKS_MODAL_REMOVED_STUDENTS_1')}
                {studentDataListTransformed.length}&nbsp;
                {this.props.t('OF')}
                {selected.length}&nbsp;
                {this.props.t('SIGN_MARKS_MODAL_REMOVED_STUDENTS_3')}
              </>
            ),
            top: 100,
          });
        }
      }
    }
  };

  /* Funkcja uruchamiająca widok szczegółowy studenta */
  rowDetailsView = (studentData: any, index: number, indent: any, expanded: boolean): React.ReactElement | null => {
    if (!isTabWithForm(this.props.state)) {
      const { data, loadingDetails, errorDetails } = this.props.state.table;
      const { userColumnsSetup, columns, marksColumns } = this.props.students;
      const { typPrzydzialu } = this.props.state.leftPanel.data;
      if (expanded) {
        const currentStudentIndex = data.findIndex((student: any) => student.studentId === studentData.studentId);
        const nextStudent = currentStudentIndex + 1;
        const prevStudent = currentStudentIndex - 1;
        if (studentData.details && studentData.marks) {
          const nestedCommonTableData = { ...studentData.details, ...studentData.marks };
          const czyKierownik: boolean =
            typPrzydzialu !== 'przydzial_podstawowy' ? true : studentData.dostep ? studentData.dostep.czyKierownik : true;
          if (this.props.scrollShouldBeFired) {
            this.props.scrollToTableRow(studentData.key, this.tableId);
          }
          return (
            <StudentDetailsView
              columns={this.props.columnDataWithTranslation(
                userColumnsSetup.details,
                removeUnwantedColumns(columns, ['ocenaWyliczona', 'ocenaKoncowaText', 'studentZdjecie']),
              )}
              marksColumns={this.props.columnDataWithTranslation(mandatoryMarksColumns, marksColumns)}
              data={TableCellTypes.generateTableContent([nestedCommonTableData], columns)}
              marksData={[this.generateContent(studentData.marks, marksColumns, studentData)]}
              translations={studentDetailsViewTranslations}
              onChangeLogCheck={this.props.openChangeLogModal.bind(
                null,
                studentData.details.przedmiotKod,
                studentData.kartaOcenId,
              )}
              openSignModal={this.openModal}
              marks={studentData.marks}
              czyKierownik={czyKierownik}
              goToNextStudent={this.props.moveToOtherRecord.bind(null, nextStudent, data, this.tableId)}
              nextDisabled={nextStudent >= data.length}
              goToPrevStudent={this.props.moveToOtherRecord.bind(null, prevStudent, data, this.tableId)}
              prevDisabled={prevStudent < 0}
            />
          );
        } else if (loadingDetails)
          return <Loader style={{ position: 'relative', left: '50%', fontSize: '32px', padding: '20px' }} />;
        else if (errorDetails) return <span className='block-centered'>{this.props.t('NO_DATA')}</span>;
        else return <></>;
      } else return null;
    } else return <></>;
  };

  generateRowSelectionSetup = () => {
    if (!isTabWithForm(this.props.state)) {
      const { table } = this.props.state;
      return {
        onSelectAll: (selected: boolean, selectedRows: any, changeRows: any): void => {
          const selectedRowKeys = changeRows.map((row: any) => row.key);
          selected ? this.props.setSelected(selectedRowKeys) : this.props.setSelected([]);
        },
        onChange: (selectedRowKeys: number[]): void => {
          this.props.setSelected(selectedRowKeys);
        },
        selectedRowKeys: table.selected,
        selections: [
          {
            key: 'all',
            text: this.props.t('SELECT_STUDENTS_ALL'),
            onSelect: (changableRowKeys: any) => this.props.setSelected(changableRowKeys),
          },
          {
            key: 'none',
            text: this.props.t('SELECT_STUDENTS_NONE'),
            onSelect: (_: any) => this.props.setSelected([]),
          },
          {
            key: 'withoutFinalMark',
            text: this.props.t('SELECT_STUDENTS_WITHOUT_FINAL_MARK'),
            onSelect: (_: any) => {
              const keysToSelect = table.data.filter(student => !student.ocenaKoncowa).map(student => student.key);
              this.props.setSelected(keysToSelect);
            },
          },
          {
            key: 'withoutFinalMarkAndMine',
            text: translationNewLineSeparator(this.props.t('SELECT_STUDENTS_WITHOUT_FINAL_MARKS_AND_MINE')),
            onSelect: (_: any) => {
              const keysToSelect = table.data
                .filter(student => !student.ocenaKoncowa || student.wpisujacyOceneKoncowaId === this.props.userId)
                .map(student => student.key);
              this.props.setSelected(keysToSelect);
            },
          },
        ],
      };
    }
  };

  generateExpandableSetup = () => {
    if (!isTabWithForm(this.props.state)) {
      const { expanded, columns } = this.props.state.table;
      return {
        expandIconColumnIndex: columns.length,
        expandIcon: () => '',
        expandIconAsCell: false,
        expandRowByClick: true,
        expandedRowRender: this.rowDetailsView,
        expandedRowKeys: [expanded],
        onExpandedRowsChange: (expandedRows: any): void => {
          const alreadyExpandedRow = expandedRows.filter((row: any) => row !== expanded)[0];
          if (alreadyExpandedRow) {
            this.props.allowScrollToTableRow();
            this.props.setExpanded(alreadyExpandedRow);
            this.props.getRecordDetails(alreadyExpandedRow);
          } else {
            this.props.setExpanded(null);
          }
        },
        defaultExpandedRowKeys: [expanded],
      };
    }
  };

  generateSingleReport = (): void => {
    const { przedmiotKod, rokAkademicki, semestrAkademicki, typPrzydzialu, wydzialZlecajacyKod } =
      this.props.state.leftPanel.data;
    const poraRoku = StringBuilder.buildSemester(rokAkademicki, semestrAkademicki);
    const kodPrzedmiotu = przedmiotKod.replaceAll(' ', '-');
    const kodWydzialu = wydzialZlecajacyKod;
    this.props.generateSingleProtocol({ poraRoku, kodPrzedmiotu, typPrzydzialu, kodWydzialu });
    this.props.openProtocolModal(poraRoku);
  };

  generateContextSearch = (loading: boolean, search: string | null): React.ReactNode => (
    <ContextSearch
      onChange={this.props.onFrontSearchChange.bind(null, this.props.setSearch)}
      disabled={loading}
      defaultValue={search}
      placeholder={this.props.t('START_TYPING_SHORT')}
      data-cy='contextSearch_subjectPage'
    />
  );

  generateFiltersDOM = () => {
    if (!isTabWithForm(this.props.state)) {
      const { filters, loading } = this.props.state.table;
      return (
        <Filter
          filterGroups={this.props.convertDataForFilters(filters.allFilters)}
          onFilterSelection={this.props.openFilterCategory}
          onFilterChange={this.onDataFilter}
          onFilterDisable={this.props.closeFilterCategory}
          translations={filterTranslations}
          loadingFilterList={filters.loading || loading || !filters.isInitialized}
        />
      );
    }
  };

  render(): React.ReactElement {
    if (!isTabWithForm(this.props.state)) {
      const { table, leftPanel, studentWithMarksData } = this.props.state;
      return (
        <CustomScrollbarContainer>
          <DynamicTabContainer>
            {this.props.showAsideButton ? (
              <MobileAside opened={this.props.openedMobileAside}>
                <MySubjectsDetailsList data={leftPanel.data} loading={leftPanel.loading} columns={this.props.columns} />
                <AboveTableContainer>{this.generateFiltersDOM()}</AboveTableContainer>
              </MobileAside>
            ) : (
              <MySubjectsDetailsList data={leftPanel.data} loading={leftPanel.loading} columns={this.props.columns} />
            )}
            <TabRightPanel withFullWidth={this.props.showAsideButton}>
              <AboveTableContainer>
                {this.props.showAsideButton ? (
                  <BurgerBtn onClick={this.props.toggleAside} opened={this.props.openedMobileAside} />
                ) : null}
                {this.props.showAsideButton && (
                  <FlexBox justify='flex-end'>{this.generateContextSearch(table.loading, table.search)}</FlexBox>
                )}
                <FlexBox justify='space-between'>
                  <FlexBox>
                    <ReactToPrint
                      documentTitle={deburr(
                        `${leftPanel.data?.przedmiotNazwa}_${leftPanel.data?.przedmiotKod}_${leftPanel.data?.rokAkademicki}${
                          leftPanel.data?.semestrAkademicki
                        }_${translationFromFile('STUDENTS_LIST')}_${moment().format('DD-MM-YYYY')}`.replaceAll(' ', '-'),
                      )}
                      trigger={() => (
                        <Tooltip title={!table.data.length ? null : this.props.t('PRINT_STUDENTS_LIST')}>
                          <span>
                            <Button
                              outlined
                              disabled={!table.data.length}
                              shape='circle'
                              icon={<TeamOutlined />}
                              data-cy='btnPrintStudentList'
                            />
                          </span>
                        </Tooltip>
                      )}
                      content={() => this.printableRef.current}
                    />
                    <Tooltip
                      title={
                        (table.totalRecords === 0 && table.loading) ||
                        !Object.keys(leftPanel.data).length ||
                        (!table.loading && !table.data.length)
                          ? null
                          : this.props.t('GET_STUDENTS_LIST_EXCEL')
                      }
                    >
                      <span>
                        <Button
                          outlined
                          shape='circle'
                          icon={<FileExcelOutlined />}
                          onClick={this.props.getDataCsv}
                          disabled={
                            (table.totalRecords === 0 && table.loading) ||
                            !Object.keys(leftPanel.data).length ||
                            (!table.loading && !table.data.length)
                          }
                          data-cy='btnGetStudentListExcel'
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        !table.data.length || studentWithMarksData?.loading
                          ? null
                          : this.props.t('PRINT_STUDENTS_LIST_WITH_MARKS')
                      }
                    >
                      <span>
                        <Button
                          outlined
                          disabled={!table.data.length || studentWithMarksData?.loading}
                          shape='circle'
                          icon={studentWithMarksData?.loading ? <LoadingOutlined style={{ margin: 0 }} /> : <FileTextOutlined />}
                          onClick={() => this.props.getDataStudentListWithMarks(this.props.id)}
                          data-cy='btnPrintStudentListWithMarks'
                        />
                      </span>
                    </Tooltip>
                    <ReactToPrint
                      documentTitle={deburr(
                        `${leftPanel.data?.przedmiotNazwa}_${leftPanel.data?.przedmiotKod}_${leftPanel.data?.rokAkademicki}${
                          leftPanel.data?.semestrAkademicki
                        }_${translationFromFile('STUDENTS_LIST_WITH_MARKS')}_${moment().format('DD-MM-YYYY')}`.replaceAll(
                          ' ',
                          '-',
                        ),
                      )}
                      content={() => this.studentsListWithMarksRef.current}
                    >
                      <PrintContextConsumer>
                        {({ handlePrint }) => <button ref={this.buttonStudentsListWithMarksRef} hidden onClick={handlePrint} />}
                      </PrintContextConsumer>
                    </ReactToPrint>
                    <ReactToPrint
                      documentTitle={deburr(
                        `${leftPanel.data?.przedmiotNazwa}_${leftPanel.data?.przedmiotKod}_${leftPanel.data?.rokAkademicki}${
                          leftPanel.data?.semestrAkademicki
                        }_${translationFromFile('ATTENDANCE_LIST')}_${moment().format('DD-MM-YYYY')}`.replaceAll(' ', '-'),
                      )}
                      trigger={() => (
                        <Tooltip title={!table.data.length ? null : this.props.t('PRINT_ATTENDANCE_LIST')}>
                          <span>
                            <Button
                              outlined
                              disabled={!table.data.length}
                              shape='circle'
                              icon={<SolutionOutlined />}
                              data-cy='btnPrintAttendanceList'
                            />
                          </span>
                        </Tooltip>
                      )}
                      content={() => this.attendanceListRef.current}
                    />
                  </FlexBox>
                  {this.props.showAsideButton && (
                    <FlexBox>
                      <DynamicButtonContainer
                        onMarksSigning={this.openModalForGroupAction}
                        onProtocolGeneration={this.generateSingleReport}
                        translations={tableDetailsViewTranslations}
                        /*
                          dla przydziałów innych niż podstawowy zawsze umożliwiamy grupowe podpisywanie ocen.
                          Dla przydziału podstawowego umożliwiamy podpisanie ocen tylko kierownikowi.
                        */
                        canSigning={
                          leftPanel.data.typPrzydzialu !== 'przydzial_podstawowy'
                            ? true
                            : DataDestructuring.czyKierownik(table.data)
                        }
                        isStudentMarked={table.selected.length >= 1}
                        isTableEmpty={table.data.length === 0}
                        signDisplay
                        onFinalMarkCopy={this.props.getStudentsToCopyFinalMarkModal}
                        displayGenerateProtocol
                      />
                    </FlexBox>
                  )}
                  {!this.props.showAsideButton && this.generateContextSearch(table.loading, table.search)}
                </FlexBox>
                {this.props.showAsideButton ? null : (
                  <FilterContainer>
                    {this.generateFiltersDOM()}
                    <DynamicButtonContainer
                      onMarksSigning={this.openModalForGroupAction}
                      onProtocolGeneration={this.generateSingleReport}
                      translations={tableDetailsViewTranslations}
                      canSigning={
                        leftPanel.data.typPrzydzialu !== 'przydzial_podstawowy'
                          ? true
                          : DataDestructuring.czyKierownik(table.data)
                      }
                      isStudentMarked={table.selected.length >= 1}
                      isTableEmpty={table.data.length === 0}
                      signDisplay
                      onFinalMarkCopy={this.props.getStudentsToCopyFinalMarkModal}
                      displayGenerateProtocol
                    />
                  </FilterContainer>
                )}
                <FlexBox justify='space-between' alignItems='flex-end'>
                  <TableHeader
                    data={this.props.generateTableHeaders(table.totalRecords, table.data.length, table.selected.length)}
                  />
                  <CacheInfo
                    synInProgress={table.cache.syncInProgress}
                    isUpToDate={table.cache.isUpToDate}
                    error={table.cache.error}
                  />
                </FlexBox>
              </AboveTableContainer>
              <div className='relative'>
                <TableContainer loading={table.loading || this.props.students.loading}>
                  <Button
                    iconButton
                    noPadding
                    outlined
                    disabled={!this.props.students.columns.length || !table.data.length}
                    className='btn_no_hover'
                    data-cy='btnColumnFilter_subjectPage'
                  >
                    <Icon
                      className={`icon_column-filter-modal ${table.loading ? 'hidden' : ''}`}
                      onClick={this.props.openModal.bind(null, 'filterColumns')}
                      path={filterColumnsModal}
                      dataCy='btnModalColumnsFilterOpen_subjectPage'
                    />
                  </Button>
                  <ColumnsFilterModal
                    mainColumns={this.props.convertDataForCheckboxes(
                      removeUnwantedColumns(this.props.students.columns, ['studentZdjecie']),
                    )}
                    detailsColumns={this.props.convertDataForCheckboxes(
                      removeUnwantedColumns(this.props.students.columns, [
                        'ocenaWyliczona',
                        'ocenaKoncowaText',
                        'studentZdjecie',
                      ]),
                    )}
                    selectedMainColumns={this.props.students.userColumnsSetup.main}
                    selectedDetailColumns={this.props.students.userColumnsSetup.details}
                    opened={this.props.openedModals.includes('filterColumns')}
                    onFilterConfirmation={this.props.studentSaveColumnsUserSetup}
                    onClose={this.props.closeModal}
                    translations={columnsFilterModalTranslations}
                    onError={() =>
                      notification.error({
                        message: this.props.t('COLUMNS_FILTER_MODAL_SELECT_MIN_ONE_ERROR'),
                        top: 100,
                      })
                    }
                  />
                  {table.data.length ? (
                    <Table
                      id={this.tableId}
                      data-cy='table_subjectPage'
                      tableLayout='auto'
                      columns={this.props.columnDataWithTranslation(this.props.students.userColumnsSetup.main, table.columns)}
                      dataSource={TableCellTypes.generateTableContent(table.data, table.columns)}
                      search={table.search}
                      searchableColumns={['studentImie', 'studentNazwisko', 'studentAlbum']}
                      onRow={(record: any) => ({
                        style: {
                          backgroundColor:
                            table.selected.includes(record.key) || table.expanded === record.key
                              ? variables.colorBackground
                              : variables.colorWhite,
                          cursor: 'pointer',
                        },
                      })}
                      onChange={(
                        pagination: any,
                        filters: any,
                        sorter: any,
                        extra: { currentDataSource: SingleObjectDataStructure[] },
                      ): any => {
                        this.props.changeColumnsSorting(this.props.id, { sortOrder: sorter.order, dataIndex: sorter.field });
                        this.props.setDataAttendanceListAfterSort(
                          this.props.id,
                          sorter.order ? extra.currentDataSource : table.data,
                        );
                        if (!sorter.order) {
                          this.props.setDataAfterSort(this.props.id, table.data);
                        }
                      }}
                      rowSelection={table.data.length ? this.generateRowSelectionSetup() : null}
                      expandable={table.data.length ? this.generateExpandableSetup() : null}
                      loading={{ spinning: table.loading, indicator: <Loader style={{ fontSize: '32px' }} /> }}
                      scroll={{
                        x: table.data.length ? 'max-content' : null,
                        y: this.props.showAsideButton
                          ? window.outerHeight >= 667
                            ? 'calc(100vh - 125px - 51px - 70px - 150px)'
                            : '420px'
                          : 'calc(100vh - 430px)',
                      }}
                    />
                  ) : table.loading ? null : (
                    <span style={{ width: '99%' }} className='block-centered'>
                      {this.props.t('NO_DATA')}
                    </span>
                  )}
                </TableContainer>
                {(table.loading || this.props.students.loading) && !table.data.length && (
                  <FlexBox justify='center'>
                    <Loader style={{ fontSize: '32px', padding: '20px' }} />
                  </FlexBox>
                )}
              </div>
            </TabRightPanel>
          </DynamicTabContainer>
          <PrintableBodyWrapper>
            <div ref={this.printableRef}>
              <HeaderWrapper smaller={true}>
                <p>{this.props.t('STUDENTS_LIST')}</p>
                <span>
                  {leftPanel.data?.przedmiotNazwa} ({leftPanel.data?.przedmiotKod})
                </span>
                <span>
                  {StringBuilder.buildReadableSemester(leftPanel.data?.rokAkademicki, leftPanel.data?.semestrAkademicki)}
                </span>
              </HeaderWrapper>
              <AntdTable
                id='list_to_print'
                tableLayout='auto'
                columns={table.columnsToPrint}
                dataSource={TableCellTypes.generateTableContent(table.dataAttendanceList, table.columnsToPrint)}
                pagination={false}
              />
            </div>
          </PrintableBodyWrapper>
          <PrintableBodyWrapper>
            <div ref={this.studentsListWithMarksRef}>
              <span hidden>{TableCellTypes.generateTableKeys(studentWithMarksData?.data ? studentWithMarksData?.data : [])}</span>
              <HeaderWrapper smaller={true}>
                <p>{this.props.t('STUDENTS_LIST_WITH_MARKS')}</p>
                <span>
                  {leftPanel.data?.przedmiotNazwa} ({leftPanel.data?.przedmiotKod})
                </span>
                <span>
                  {StringBuilder.buildReadableSemester(leftPanel.data?.rokAkademicki, leftPanel.data?.semestrAkademicki)}
                </span>
              </HeaderWrapper>
              <AntdTable
                id='attendance_list'
                size='small'
                tableLayout='auto'
                bordered={true}
                columns={studentWithMarksData?.columns}
                dataSource={TableCellTypes.generateTableContent(
                  studentWithMarksData?.data ? studentWithMarksData?.data : [],
                  studentWithMarksData?.columns ? studentWithMarksData?.columns : studentListWithMarksColumns,
                )}
                pagination={false}
              />
            </div>
          </PrintableBodyWrapper>
          <PrintableBodyWrapper>
            <div ref={this.attendanceListRef}>
              <HeaderWrapper smaller={false}>
                <p>{this.props.t('ATTENDANCE_LIST')}</p>
                <span>
                  {leftPanel.data?.przedmiotNazwa} ({leftPanel.data?.przedmiotKod})
                </span>
                <span>
                  {StringBuilder.buildReadableSemester(leftPanel.data?.rokAkademicki, leftPanel.data?.semestrAkademicki)}
                </span>
              </HeaderWrapper>
              <AntdTable
                size='small'
                id='attendance_list'
                tableLayout='auto'
                bordered={true}
                columns={table.columnsToAttendanceList}
                dataSource={TableCellTypes.generateTableContent(table.dataAttendanceList, table.columnsToAttendanceList)}
                pagination={false}
              />
            </div>
          </PrintableBodyWrapper>
        </CustomScrollbarContainer>
      );
    } else return <></>;
  }
}

type MapStateToProps = ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: RootState) => ({
  columns: state.StaticViews.MySubjects.columns,
  students: state.StaticViews.Students,
  userId: state.Auth.user.id ? Number(state.Auth.user.id) : null,
});

type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;

const mapDispatchToProps = (dispatch: any, { id }: DynamicTabComponent) => ({
  studentSaveColumnsUserSetup: (selectedColumns: SelectedColumns) =>
    dispatch(studentSaveColumnsUserSetup('Students', selectedColumns)),
  getDataCsv: () => dispatch(getDataCsv(id)),
  openSignModal: (data: MarksData) => dispatch(openSignModal(data)),
  getStudentsToCopyFinalMarkModal: () => dispatch(getStudentsToCopyFinalMarkModal(id)),
  openChangeLogModal: (kodPrzedmiotu: string, kartaPrzedmiotu: string) =>
    dispatch(openChangeLogModal(kodPrzedmiotu, kartaPrzedmiotu)),
  openProtocolModal: (poraRoku: Protocol.Basic['poraRoku']) => dispatch(openProtocolModal(poraRoku)),
  generateSingleProtocol: (params: Protocol.ParamsForSingle) => dispatch(generateSingleProtocol(params)),
  setDataAfterSort: (tabId: DynamicTabComponent['id'], data: SingleObjectDataStructure[]) =>
    dispatch(setDataAfterSort(tabId, data)),
  changeColumnsSorting: (tabId: DynamicTabComponent['id'], sortingInfo: Helpers.SortingInfo) =>
    dispatch(changeColumnsSorting(tabId, sortingInfo)),
  setDataAttendanceListAfterSort: (tabId: DynamicTabComponent['id'], data: SingleObjectDataStructure[]) =>
    dispatch(setDataAttendanceListAfterSort(tabId, data)),
  getDataStudentListWithMarks: (tabId: DynamicTabComponent['id']) => dispatch(getDataStudentListWithMarks(tabId)),
  getDataStudentListWithMarksCancel: (tabId: DynamicTabComponent['id']) => dispatch(getDataStudentListWithMarksCancel(tabId)),
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps),
  withCommonTableLogic,
  withTableClickableModalsHandler,
  withContextSearch,
  withAntdConfigProvider,
  withMobileAsideHandler,
  withDynamicTabLauncher,
  withDynamicTabTableActionsProvider,
  withDynamicTabStateProvider,
  withNoAccessToResource,
)(SubjectPage) as React.ReactElement;
